/**
 * hands_approval — the pure copy + color grammar for the OS/browser consent
 * cards (OS Hands P2.5a, founder-approved round 3). SINGLE source of truth so
 * the composer card (HandsApprovalInput) and the attention chip
 * (useTaskFiltering.getInteractionAttentionInfo) never drift.
 *
 * The round-3 per-app grammar is deliberately tiny — a monogram tile, a
 * verb-first question, at most one quiet line, two pills — and the TIER rides
 * the primary pill's COLOR alone (neutral = remembered medium, solid red =
 * one-shot high / takeover). No accents, no rings, no in-card fineprint. All of
 * that structure lives in the component; THIS file owns only the strings and the
 * deterministic hue. House rules: no assistant brand names, no em dashes,
 * sentence case.
 */

import {
  TAKEOVER_HID_ACTIONS,
  friendlyAppName,
  osVerbCardAction,
} from "@bash/shared";

/** The shape the copy helpers read — the OS/browser fields the gate projects. */
export interface HandsAskFields {
  lane?: string;
  firstEngage?: boolean;
  app?: string;
  url?: string;
  capability?: string;
  tool?: string;
  tier?: "low" | "medium" | "high";
  label?: string;
}

/** os_takeover / os_drag — the physical-cursor rung. A per-app takeover card can
 *  no longer be minted (the gate short-circuits it), but the grammar keeps the
 *  branch so the shape renders honestly if one ever arrives. */
export function isTakeoverAsk(fields: HandsAskFields): boolean {
  return fields.tool !== undefined && TAKEOVER_HID_ACTIONS.has(fields.tool);
}

/**
 * The friendly name that stands in for the app in every string: the OS bundle
 * leaf (`com.apple.mail` -> `Mail`, via the P2.4 helper) or, on the browser
 * lane, the origin host (`https://bank.example.com/x` -> `bank.example.com`,
 * `www.` stripped). Undefined when neither is usable — callers fall to a
 * generic phrase and NEVER invent a target.
 */
export function handsDisplayName(fields: HandsAskFields): string | undefined {
  if (fields.app) {
    const name = friendlyAppName(fields.app);
    if (name) return name;
  }
  if (fields.url) {
    try {
      return new URL(fields.url).hostname.replace(/^www\./, "") || undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** The monogram letter — the display name's first character, uppercased. Falls
 *  back to a neutral bullet when there is no name (never a guessed initial). */
export function handsMonogramLetter(fields: HandsAskFields): string {
  return handsDisplayName(fields)?.charAt(0).toUpperCase() || "#";
}

/**
 * Deterministic hue (0..359) from the app's stable identity — the bundle id, or
 * the origin on the browser lane. FNV-1a so the same app is the same color
 * forever, with no icon fetch (real app icons are unreachable from the web) and
 * no payload change.
 */
export function handsMonogramHue(fields: HandsAskFields): number {
  const seed = fields.app || fields.url || "";
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 360;
}

/** capability defaults to 'actuation' (the browser lane sends none). */
function isPixels(fields: HandsAskFields): boolean {
  return fields.capability === "pixels";
}

/** Verb-first question headline. actuation = "Work in {app}?", pixels = "See
 *  {app}'s screen?", takeover = "Take the cursor for {app}?". */
export function handsHeadline(fields: HandsAskFields): string {
  const app = handsDisplayName(fields) ?? "this app";
  if (isTakeoverAsk(fields)) return `Take the cursor for ${app}?`;
  if (isPixels(fields)) return `See ${app}'s screen?`;
  return `Work in ${app}?`;
}

/** Sentence-case the first letter of a standalone truth ("asks every time" ->
 *  "Asks every time"), leaving the rest untouched. */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The one quiet line under the headline, composed as `[<lead> ·] <tier truth>`:
 *   - takeover ⇒ the reclaim promise alone (the header meta carries the one-shot
 *     truth), "Move your mouse to take it back";
 *   - pixels ⇒ the scope leads, "One window · <tier truth>";
 *   - actuation WITH a verb id ⇒ the human action lead "To open a link · <tier
 *     truth>" (osVerbCardAction turns the id into copy; R6); with a key-chord /
 *     AX label ⇒ "To press <label> · <tier truth>";
 *   - actuation with NO label ⇒ the tier truth alone (never an invented target).
 * Tier truth is LANE-AWARE — only the OS lane persists a grant (server-side
 * addAllowedApp, medium+low only). "Won't ask again" (remembered) is honest
 * ONLY for an OS-lane non-high ask; a high OS ask is one-shot, and the browser
 * lane persists NOTHING (medium+careful re-asks every time). So off the OS lane,
 * or on a high ask, the truth is "asks every time" — claiming memory otherwise
 * would lie.
 */
export function handsQuietLine(fields: HandsAskFields): string {
  if (isTakeoverAsk(fields)) return "Move your mouse to take it back";
  const remembered = fields.lane === "os" && fields.tier !== "high";
  const tierTruth = remembered ? "won't ask again" : "asks every time";
  const lead = isPixels(fields)
    ? "One window"
    : fields.label
    ? osVerbCardAction(fields.label) ?? `To press ${fields.label}`
    : null;
  return lead ? `${lead} · ${tierTruth}` : capitalize(tierTruth);
}

/** The approve pill repeats the headline's verb, EXCEPT the fail-closed case:
 *  an unreadable actuation target escalates to high and narrows the grant to a
 *  single press, "Allow this press". */
export function handsApproveLabel(fields: HandsAskFields): string {
  if (isTakeoverAsk(fields)) return "Take the cursor";
  const app = handsDisplayName(fields);
  if (isPixels(fields)) return app ? `See ${app}'s screen` : "See the screen";
  if (fields.tier === "high" && !fields.label) return "Allow this press";
  return app ? `Work in ${app}` : "Work here";
}

/** The deny pill copy — safe-voiced. Takeover keeps the user's cursor; every
 *  other ask is a polite "Not now". */
export function handsDenyLabel(fields: HandsAskFields): string {
  return isTakeoverAsk(fields) ? "Keep my cursor" : "Not now";
}

/** The header's right-side meta. "this step only" is TAKEOVER-ONLY (mockup):
 *  its quiet line is the reclaim promise, so the meta carries the one-shot
 *  truth. Every other high ask already says "asks every time" in its quiet
 *  line, and the meta stays the default. */
export function handsHeaderMeta(fields: HandsAskFields): string {
  if (fields.firstEngage) return "one-time setup";
  if (isTakeoverAsk(fields)) return "this step only";
  return "needs your OK";
}

/** The tier cue is the primary pill color ALONE: solid red for a one-shot high
 *  / takeover ask, neutral for a remembered medium. */
export function handsApproveIsDanger(fields: HandsAskFields): boolean {
  return isTakeoverAsk(fields) || fields.tier === "high";
}

/**
 * The attention-chip preview — the glanceable one-liner on the task card /
 * board when an OS ask is pending but the composer isn't open. Same grammar,
 * compressed to a statement: "Mail is asking to work" / "Safari wants to see its
 * screen" / the first-engage lane-on line.
 */
export function handsAttentionPreview(fields: HandsAskFields): string {
  if (fields.firstEngage) return "Turn on Hands for your Mac";
  const app = handsDisplayName(fields) ?? "An app";
  if (isTakeoverAsk(fields)) return `${app} wants to take the cursor`;
  if (isPixels(fields)) return `${app} wants to see its screen`;
  return `${app} is asking to work`;
}
